var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from "react";
import * as SavvyResultsUI from "../../components/SavvyResultsUI/SavvyResultsUI";
import { offerPresented, sawImpression } from "../../lib/Avo";
import { useDidMount, useEffectOnceWhen } from "rooks";
import Ad from "../../components/Ad/Ad";
import AutoBundleItem from "../../features/search/components/AutoBundleItem";
import EditPolicyButton from "../../features/search/components/EditPolicyButton";
import Header from "../../components/Header";
import { ImpressionType } from "../../lib/constants";
import { InsuranceType } from "../../constants";
import NoQuotes from "../../features/search/components/NoQuotes";
import NoSavings from "../../features/search/components/NoSavings";
import { PHASE } from "../../routeConfig";
import { PivotReason } from "../../features/applicant/applicantSlice";
import Quote from "../../components/Quote";
import { ResultType } from "../../services/savvy/constants";
import { SellingPointGroups } from "../../features/search/components/SellingPoints";
import { convertCentsToDollars } from "../../lib/util";
import { createSelector } from "@reduxjs/toolkit";
import { filterAdResultsForAnalytics } from "../../features/search/utils";
import { getMonthlyAutoPremium } from "../../services/savvy/utils";
import { getQuoteWithMostSavings } from "../../utils/getQuoteWithMostSavings";
import { skipToken } from "@reduxjs/toolkit/query";
import useAppSelector from "../../hooks/useAppSelector";
import { useGetSearchResultsQuery } from "../../services/savvy";
import useImpressionType from "../../services/savvy/useImpressionType";
import useIsRemarketingSearch from "../../features/configuration/useIsRemarketingSearch";
import useResultsHeaderText from "../../features/search/useResultsHeaderText";
var selectApplicantData = createSelector([function (state) { return state.applicant; }], function (_a) {
    var accountLinked = _a.accountLinked, accountReferenceId = _a.accountReferenceId, bundlePivotReason = _a.bundlePivotReason, currentIssuer = _a.currentIssuer;
    return {
        selectedIssuer: currentIssuer.auto,
        accountLinked: accountLinked,
        accountReferenceId: accountReferenceId,
        bundlePivotReason: bundlePivotReason,
    };
});
export default function AutoOfferListRoute() {
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var _a = useAppSelector(selectApplicantData), accountLinked = _a.accountLinked, accountReferenceId = _a.accountReferenceId, bundlePivotReason = _a.bundlePivotReason;
    var isRemarketingSearch = useIsRemarketingSearch();
    var impressionType = useImpressionType();
    var _b = useGetSearchResultsQuery(accountReferenceId
        ? {
            accountReferenceId: accountReferenceId,
            isRemarketingSearch: isRemarketingSearch,
            impressionType: impressionType,
        }
        : skipToken, {
        selectFromResult: function (_a) {
            var data = _a.data, isSuccess = _a.isSuccess;
            var reducedBundles = data === null || data === void 0 ? void 0 : data.bundles.reduce(function (acc, curr) {
                var hasSameIssuer = function (item) {
                    return (item.auto.advertiserName &&
                        item.auto.advertiserName === curr.auto.advertiserName) ||
                        (Boolean(item.auto.issuerId) &&
                            item.auto.issuerId === curr.auto.issuerId);
                };
                if (acc.findIndex(hasSameIssuer) !== -1) {
                    return acc;
                }
                return __spreadArray(__spreadArray([], acc, true), [curr], false);
            }, []);
            var hasOnlyBundleAutoAds = reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.every(function (result) { return result.auto.type === ResultType.Ad; });
            var hasBundleSavings = reducedBundles !== undefined &&
                reducedBundles.every(function (bundle) {
                    return bundle.auto.type === ResultType.CurrentAutoPolicy &&
                        bundle.home.type === ResultType.CurrentHomePolicy;
                }) === false;
            // We should only show the user's current auto policy as an option IF
            // there is an actual policy attached to it - not just the user's own home policy
            var hasValidAutoCurrentPolicy = reducedBundles !== undefined &&
                reducedBundles.findIndex(function (bundle) {
                    return bundle.auto.type === ResultType.CurrentAutoPolicy &&
                        bundle.home.type !== ResultType.CurrentHomePolicy;
                }) !== -1;
            return {
                currentPlan: data === null || data === void 0 ? void 0 : data.currentPlan,
                footerContent: data === null || data === void 0 ? void 0 : data.footerContent,
                mixedResults: data === null || data === void 0 ? void 0 : data.mixedResults,
                impressionId: data === null || data === void 0 ? void 0 : data.impressionId,
                quotes: data === null || data === void 0 ? void 0 : data.quotes,
                reducedBundles: hasValidAutoCurrentPolicy
                    ? reducedBundles
                    : reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.filter(function (bundle) { return bundle.auto.type !== ResultType.CurrentAutoPolicy; }),
                hasBundleSavings: hasBundleSavings,
                hasValidAutoCurrentPolicy: hasValidAutoCurrentPolicy,
                hasOnlyBundleAutoAds: hasOnlyBundleAutoAds,
                isSuccess: isSuccess,
            };
        },
    }), currentPlan = _b.currentPlan, footerContent = _b.footerContent, mixedResults = _b.mixedResults, impressionId = _b.impressionId, quotes = _b.quotes, reducedBundles = _b.reducedBundles, hasBundleSavings = _b.hasBundleSavings, hasOnlyBundleAutoAds = _b.hasOnlyBundleAutoAds, isSuccess = _b.isSuccess;
    var headerText = useResultsHeaderText();
    var monthlyAutoPremium = getMonthlyAutoPremium(currentPlan);
    var totalMonthlyCostCents = (monthlyAutoPremium || 0) + ((currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.homeMonthlyPremiumCents) || 0);
    var cheapestQuote = quotes ? getQuoteWithMostSavings(quotes) : null;
    var sawSavings = convertCentsToDollars(cheapestQuote === null || cheapestQuote === void 0 ? void 0 : cheapestQuote.averageMonthlySavingsCents) > 0;
    var showBundles = impressionType === ImpressionType.AutoHome;
    var isRenter = bundlePivotReason === PivotReason.Rent;
    var hasResults = showBundles
        ? Boolean(reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.length)
        : Boolean(mixedResults === null || mixedResults === void 0 ? void 0 : mixedResults.length);
    var showBundleBreadcrumbs = showBundles && hasBundleSavings && hasResults && !hasOnlyBundleAutoAds;
    var shouldShowMixedResultsAdsForBundle = showBundles && !hasResults && Boolean(mixedResults === null || mixedResults === void 0 ? void 0 : mixedResults.length);
    useDidMount(function () {
        sawImpression(__assign(__assign({}, commonAnalyticsProperties), { impressionRequest: {
                hasLinkedAccount: accountLinked.auto,
                insuranceType: "auto",
                forceFormat: "",
                medium: "online",
            }, impression: {
                insuranceType: "auto",
                format: "",
                searchedProductTypes: [],
            } }));
    });
    useEffectOnceWhen(function () {
        var filteredAutoAds = filterAdResultsForAnalytics(mixedResults);
        var ads = impressionType === ImpressionType.AutoHome
            ? reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.filter(function (item) { return item.auto.type === ResultType.Ad; })
            : filteredAutoAds;
        var quotes = impressionType === ImpressionType.AutoHome
            ? reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.filter(function (item) { return item.auto.type === ResultType.Quote; })
            : mixedResults === null || mixedResults === void 0 ? void 0 : mixedResults.filter(function (result) { return Boolean(result.quoteContent); });
        offerPresented(__assign(__assign({}, commonAnalyticsProperties), { impressionId: impressionId, accountReferenceId: accountReferenceId, sawSavings: sawSavings, impressionType: impressionType, ads: ads, quotes: quotes, homeAds: null, homeQuotes: null }));
    }, isSuccess);
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { showDropShadow: true },
            React.createElement(React.Fragment, null,
                React.createElement("div", { className: "text-center" },
                    React.createElement("div", { className: "text-xs text-black-300" }, "Your policy"),
                    React.createElement("div", { className: "text-xs font-semibold text-black-100" }, headerText)),
                React.createElement(EditPolicyButton, { nextRoute: PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES }))),
        React.createElement(SavvyResultsUI.Root, null,
            showBundleBreadcrumbs ? React.createElement(SavvyResultsUI.BundleBreadcrumbs, null) : null,
            hasResults || shouldShowMixedResultsAdsForBundle ? (showBundles &&
                !hasBundleSavings &&
                !shouldShowMixedResultsAdsForBundle ? (React.createElement(NoSavings, null)) : (React.createElement(SavvyResultsUI.Body, { isRenter: isRenter, className: "bg-[#F7F7F7]" },
                React.createElement(SavvyResultsUI.List, null, showBundles && !shouldShowMixedResultsAdsForBundle
                    ? reducedBundles === null || reducedBundles === void 0 ? void 0 : reducedBundles.map(function (result, index) { return (React.createElement(AutoBundleItem, { key: result.id, result: result, index: index, impressionId: impressionId, totalMonthlyCostCents: totalMonthlyCostCents, showBundles: showBundles })); })
                    : mixedResults === null || mixedResults === void 0 ? void 0 : mixedResults.map(function (_a, index) {
                        var adContent = _a.adContent, quoteContent = _a.quoteContent;
                        return (React.createElement(React.Fragment, { key: index },
                            React.createElement(SavvyResultsUI.ListItem, null, adContent ? (React.createElement(Ad, { ad: adContent, index: index, impressionId: impressionId || "" })) : quoteContent ? (React.createElement(Quote, { quote: quoteContent, impressionId: impressionId || "", index: index, bottomContent: quoteContent.sellingPointGroups &&
                                    quoteContent.sellingPointGroups.length > 0 ? (React.createElement(SellingPointGroups, { groups: quoteContent.sellingPointGroups })) : null, insuranceType: InsuranceType.Auto })) : null)));
                    })),
                footerContent ? (React.createElement(SavvyResultsUI.FooterContent, null,
                    "Insurance Quotes Provided by:",
                    React.createElement("br", null),
                    footerContent.agency,
                    React.createElement("br", null),
                    footerContent.licenseText)) : null))) : (React.createElement(NoQuotes, null)),
            React.createElement(SavvyResultsUI.Footer, null))));
}
